import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { clearfixStyles, editLinkStyles } from "@store/theme/common/mixins";
import { checkoutTitleProps } from "@store/theme/common/props/checkout";
import getWidgetComponent from "@store/utilities/getWidgetComponent";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

const PageTitle = getWidgetComponent("Basic/PageTitle");

interface Props extends WidgetProps {}

export interface BrandDetailsHeaderStyles {
    container?: TypographyPresentationProps;
    wrapper?: TypographyPresentationProps;
    wrapperItem?: InjectableCss;
    linkButton?: LinkPresentationProps;
}

export const brandHeaderStyles: BrandDetailsHeaderStyles = {
    container: {
        css: css`
            ${checkoutTitleProps.wrapper?.css}
            ${clearfixStyles()}
            display: block;
        `,
    },
    wrapper: {
        forwardAs: "div",
        variant: "body",
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            flex-flow: row wrap;
            margin: 0.5em 0 0 -1.5em;
        `,
    },
    wrapperItem: {
        css: css`
            display: flex;
            margin: 0.5em 0 0 1.5em;
        `,
    },
    linkButton: {
        css: css`
            ${editLinkStyles()}
            display: block;
            margin-left: 1em;

            &:first-child {
                margin-left: 0;
            }
        `,
    },
};

const styles = brandHeaderStyles;

const BrandHeader: FC<Props> = () => {
    const { value: brand } = useContext(BrandStateContext);

    if (!brand) {
        return null;
    }

    return (
        <Typography {...styles.container}>
            <Typography {...styles.wrapper}>
                <StyledWrapper {...styles.wrapperItem}>
                    <PageTitle overrideTitle={brand.name} />
                </StyledWrapper>
                <StyledWrapper {...styles.wrapperItem}>
                    <Link
                        data-test-selector="brandToProductPageLink"
                        href={brand.productListPagePath}
                        {...styles.linkButton}
                    >
                        {translate("Shop All {0} Products").replace("{0}", brand.name)}
                    </Link>
                    {brand.externalUrl && (
                        <Link data-test-selector="brandExternalUrlLink" href={brand.externalUrl} {...styles.linkButton}>
                            {translate("Visit {0} Website").replace("{0}", brand.name)}
                        </Link>
                    )}
                </StyledWrapper>
            </Typography>
        </Typography>
    );
};

const widgetModule: WidgetModule = {
    component: BrandHeader,
    definition: {
        group: "Brand Details",
        displayName: "Brand Header",
        allowedContexts: ["BrandDetailsPage"],
    },
};

export default widgetModule;
